import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { trackDeckExported } from '../analytics';
import { loadActiveDeckLogoUrl, loadActiveDeckName } from '../data/deckStore';
import { loadSlides } from '../data/slidesStore';
import { appTheme, pitchTheme } from '../styles/theme';
import { captureElementToCanvas, exportCanvasesToPdf } from '../utils/exportDeckPdf';
import { previewReturnPath } from '../utils/previewReturn';
import PitchCard from './PitchCard';

const Page = styled.main`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  background: ${pitchTheme.pageGradient};
`;

const PreviewActionButton = styled.button`
  position: fixed;
  top: 20px;
  z-index: 20;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusPill};
  background: rgba(250, 250, 248, 0.94);
  backdrop-filter: blur(12px);
  color: ${appTheme.text};
  font-family: ${pitchTheme.fontUi};
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: ${appTheme.shadowSm};
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${appTheme.green};
    background: ${appTheme.mint};
  }

  &:disabled {
    opacity: 0.65;
    cursor: wait;
  }

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
  }
`;

const BackButton = styled(PreviewActionButton)`
  left: 20px;
`;

const DownloadPdfButton = styled(PreviewActionButton)`
  right: 20px;
`;

const CarouselShell = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  width: min(1040px, 100%);
`;

const SideTab = styled.button<{ $side: 'left' | 'right' }>`
  flex: 0 0 36px;
  border: none;
  background: ${pitchTheme.navy};
  color: #fff;
  cursor: pointer;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: ${({ $side }) => ($side === 'left' ? 'rotate(180deg)' : 'none')};
  font-family: ${pitchTheme.fontUi};
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 16px 0;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: ${pitchTheme.blueDark};
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  @media (max-width: 768px) {
    flex-basis: 28px;
    font-size: 0.5rem;
  }
`;

const Viewport = styled.div<{ $pdfCapture?: boolean }>`
  flex: 1;
  overflow: hidden;

  ${({ $pdfCapture }) =>
    $pdfCapture &&
    `
    [data-pdf-slide] *::before,
    [data-pdf-slide] *::after {
      background-image: none !important;
      background-size: auto !important;
    }
  `}
`;

const Track = styled.div<{ $index: number; $instant?: boolean }>`
  display: flex;
  transition: ${({ $instant }) =>
    $instant ? 'none' : 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)'};
  transform: translateX(${({ $index }) => `-${$index * 100}%`});
`;

const Slide = styled.div`
  flex: 0 0 100%;
  display: flex;
  justify-content: center;
  padding: 0 8px;
`;

const Dots = styled.div`
  position: absolute;
  left: 50%;
  bottom: -36px;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
`;

const Dot = styled.button<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  background: ${({ $active }) => ($active ? pitchTheme.blue : '#CFE3F1')};
  transition: transform 0.2s ease, background 0.2s ease;

  &:hover {
    transform: scale(1.15);
  }
`;

const PitchCarousel: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = previewReturnPath(location.state);
  const [slides] = useState(() => loadSlides());
  const [logoUrl] = useState(() => loadActiveDeckLogoUrl());
  const [deckName] = useState(() => loadActiveDeckName());
  const [index, setIndex] = useState(0);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isPdfExport, setIsPdfExport] = useState(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const total = slides.length;

  const waitForPaint = () =>
    new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });

  const goBack = useCallback(() => {
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/decks');
  }, [navigate, returnTo]);

  const goTo = useCallback(
    (nextIndex: number) => {
      if (nextIndex < 0 || nextIndex >= total) {
        return;
      }
      setIndex(nextIndex);
    },
    [total],
  );

  const goPrev = useCallback(() => goTo(index - 1), [goTo, index]);
  const goNext = useCallback(() => goTo(index + 1), [goTo, index]);

  const returnLabel = (() => {
    switch (returnTo) {
      case '/edit':
        return t('preview.backToEditor');
      case '/decks':
        return t('preview.backToDecks');
      case '/':
        return t('preview.backToTemplates');
      default:
        return t('preview.back');
    }
  })();

  const handleDownloadPdf = useCallback(async () => {
    if (isExportingPdf || slides.length === 0) {
      return;
    }

    setIsExportingPdf(true);
    setIsPdfExport(true);

    const captureHost = document.createElement('div');
    captureHost.dataset.pdfCaptureHost = '';
    captureHost.style.cssText =
      'position:fixed;left:-10000px;top:0;width:920px;height:520px;overflow:hidden;pointer-events:none;';
    document.body.appendChild(captureHost);

    let captureRoot: Root | null = createRoot(captureHost);

    try {
      await document.fonts.ready;
      const canvases: HTMLCanvasElement[] = [];

      for (let slideIndex = 0; slideIndex < slides.length; slideIndex += 1) {
        flushSync(() => {
          captureRoot?.render(
            <PitchCard
              slide={slides[slideIndex]}
              imageOnRight={slideIndex % 2 === 0}
              logoUrl={logoUrl}
              pdfCapture
            />,
          );
        });
        await waitForPaint();

        const card = captureHost.querySelector<HTMLElement>('[data-pdf-slide]');
        if (!card) {
          throw new Error(t('preview.captureSlideFailed', { index: slideIndex + 1 }));
        }

        canvases.push(await captureElementToCanvas(card));
      }

      exportCanvasesToPdf(canvases, deckName);
      trackDeckExported(slides.length);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : t('preview.couldNotExport'));
    } finally {
      captureRoot?.unmount();
      captureRoot = null;
      captureHost.remove();
      setIsPdfExport(false);
      setIsExportingPdf(false);
    }
  }, [deckName, isExportingPdf, logoUrl, slides, t]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        goBack();
        return;
      }
      if (event.key === 'ArrowLeft') {
        goPrev();
      }
      if (event.key === 'ArrowRight') {
        goNext();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goBack, goNext, goPrev]);

  const prevSlide = index > 0 ? slides[index - 1] : null;
  const nextSlide = index < total - 1 ? slides[index + 1] : null;

  return (
    <Page>
      <BackButton type="button" onClick={goBack}>
        ← {returnLabel}
      </BackButton>
      <DownloadPdfButton
        type="button"
        onClick={() => void handleDownloadPdf()}
        disabled={isExportingPdf || slides.length === 0}
      >
        {isExportingPdf ? t('preview.preparingPdf') : t('preview.downloadPdf')}
      </DownloadPdfButton>
      <CarouselShell>
        <SideTab
          type="button"
          $side="left"
          onClick={goPrev}
          disabled={!prevSlide}
          aria-label={
            prevSlide ? t('preview.previousSlide', { title: prevSlide.title }) : t('preview.noPreviousSlide')
          }
        >
          {prevSlide ? `${t('preview.previous')} ${prevSlide.tabLabel}` : t('preview.previous')}
        </SideTab>

        <Viewport ref={viewportRef} $pdfCapture={isPdfExport} aria-live="polite">
          <Track $index={index} $instant={isPdfExport}>
            {slides.map((slide, slideIndex) => (
              <Slide key={slide.id}>
                <PitchCard slide={slide} imageOnRight={slideIndex % 2 === 0} logoUrl={logoUrl} />
              </Slide>
            ))}
          </Track>
        </Viewport>

        <SideTab
          type="button"
          $side="right"
          onClick={goNext}
          disabled={!nextSlide}
          aria-label={
            nextSlide ? t('preview.nextSlide', { title: nextSlide.title }) : t('preview.noNextSlide')
          }
        >
          {nextSlide ? `${t('preview.next')} ${nextSlide.tabLabel}` : t('preview.next')}
        </SideTab>

        <Dots role="tablist" aria-label={t('preview.pitchSlides')}>
          {slides.map((slide, dotIndex) => (
            <Dot
              key={slide.id}
              type="button"
              role="tab"
              aria-selected={dotIndex === index}
              aria-label={t('preview.goToSlide', { index: dotIndex + 1, title: slide.title })}
              $active={dotIndex === index}
              onClick={() => goTo(dotIndex)}
            />
          ))}
        </Dots>
      </CarouselShell>
    </Page>
  );
};

export default PitchCarousel;
